import type { IssuanceMetadataPaths } from '@safc/configuration';
import * as z from 'zod';

import {
  feedstockDatabase,
  feedstockOther,
} from './feedstock-db.js';

const feedstockZods = feedstockDatabase.map(feedstock => z.object({
  name: z.literal<string>(feedstock.name),
  isSolidWaste: z.literal<boolean>(feedstock.isSolidWaste),
  wasCertifiedAsProductionResidue: z.literal<boolean>(feedstock.wasCertifiedAsProductionResidue),
  /** [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing */
  isEnergyCrop: z.literal<boolean>(feedstock.isEnergyCrop),
  /** [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing */
  isLanduseBasedCropBased: z.literal<boolean>(feedstock.isLanduseBasedCropBased),
  /** [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing */
  isCorsiaDefaultIlucLcaLteZero: z.boolean(),
  /** [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing */
  isInEuRed2Annex9PartA: z.boolean(),
  ...(feedstock.name === feedstockOther.name ? ({ otherFeedStock: z.string().min(1) }) : {} as Record<string, never>),
}));

type AnyFeedstockType = typeof feedstockZods[number];
export type AnyFeedstock = z.infer<typeof feedstockZods[number]>;

export const ZodAnyFeedstock = z.discriminatedUnion('name', feedstockZods as AnyFeedstockType[] as [AnyFeedstockType]);

/**
 * Selecting a feedstock should preset few feedstock-related fields that we hard-save in unit metadata
 */
export const getFeedstockFieldsToSet = (feedstockName: string | undefined): [IssuanceMetadataPaths, boolean | undefined][] => {
  if (feedstockName !== undefined) {
    const feedstock = feedstockDatabase.find(entry => entry.name === feedstockName);

    if (!feedstock) {
      throw new Error(`Cannot find feedstock: ${feedstockName} in feedstock database`);
    }

    if (feedstock.name !== feedstockOther.name) {
      return [
        ['product.feedStock.isSolidWaste', feedstock.isSolidWaste],
        ['product.feedStock.wasCertifiedAsProductionResidue', feedstock.wasCertifiedAsProductionResidue],
        ['product.feedStock.isEnergyCrop', feedstock.isEnergyCrop],
        ['product.feedStock.isLanduseBasedCropBased', feedstock.isLanduseBasedCropBased],
        ['product.feedStock.isInEuRed2Annex9PartA', feedstock.isInEuRed2Annex9PartA],
        // if these feedstock fields are FALSE then these certification fields are forced to be FALSE
        // otherwise allow for decision to be made by setting undefined
        ['certification.isLowDisplacementRiskConfirmed', feedstock.wasCertifiedAsProductionResidue ? undefined : false],
        ['certification.isLowILUCRiskConfirmed', feedstock.isLanduseBasedCropBased ? undefined : false],
      ];
    }
  }

  return [];
};
